import NiceModal from '@ebay/nice-modal-react';

import { useMemo } from 'react';
import { FormProvider, useFormContext } from 'react-hook-form';

import AddressGroupV2 from 'components/field/AddressGroupV2';
import Button from 'components/field/Button';
import { ImageModal } from 'components/field/ImageViewer';
import PhoneNumber from 'components/field/PhoneNumber';
import StringInput from 'components/field/StringInput';
import FormItem from 'components/form/FormItem';
import Typography from 'components/global/Typography';
import Table from 'components/table/Table';

import useAlert from 'hooks/useAlert';

import { openAxios } from 'services/axios';
import { OTO_LINK } from 'services/consts';

import AppLogger from 'utils/AppLogger';

import { FormHeader } from '../OTOLinkBuyer';
import * as S from './Tabs.styled';

const defaultColSpan = {
	md: 6
};

const fullColSpan = {
	md: 12
};

const ContactDetailsTab = ({ nextStep }) => {
	const { showError } = useAlert();

	const formMethods = useFormContext();
	const {
		handleSubmit,
		setValue,
		watch,
		reset,
		formState: { isSubmitting, isLoading: isFormLoading, isDirty, isValid }
	} = formMethods;

	const isLoading = isFormLoading || isSubmitting;

	const onSubmit = async (data) => {
		try {
			const response = await openAxios.post(OTO_LINK.UPDATE_OTO_LINK_BUYER_CONTACT_DETAILS, {
				GUID: data.GUID,
				otoLinkSessionId: data.otoLinkSessionId,
				key: data.key,
				firstName: data.firstName,
				lastName: data.lastName,
				phone: data.phone,
				email: data.email,
				address: {
					googleAddress: data?.address?.text,
					city: data?.address?.cityName,
					country: data?.address?.countryName,
					state: data?.address?.stateName,
					zipCode: data?.address?.zipCode,
					lat: data?.address?.lat,
					lng: data?.address?.lng
				}
			});
			if (response.status !== 200) {
				throw new Error(response);
			}
			reset(data);
			nextStep();
		} catch (error) {
			AppLogger.error(error);
			showError();
		}
	};

	return (
		<FormProvider {...formMethods}>
			<S.Card padding={0} background border={0} shadowed rounded>
				<S.Wrapper>
					<div>
						<FormHeader title="enterContactAndAddressDetails" />
						<S.Accordion header="orderSummary" defaultVisible={true}>
							<div>
								<S.AccordionInner>
									<OrderTable />
								</S.AccordionInner>
							</div>
						</S.Accordion>

						<S.Content>
							<Typography variant="p1" fontWeight="700">
								personalDetails
							</Typography>
							<S.Form>
								<FormItem label="firstName" colSpan={defaultColSpan} required>
									<StringInput placeholder="firstNamePlaceholder" name="firstName" />
								</FormItem>
								<FormItem label="lastName" colSpan={defaultColSpan} required>
									<StringInput placeholder="lastNamePlaceholder" name="lastName" />
								</FormItem>
								<FormItem label="mobileNumber" colSpan={defaultColSpan} required>
									<PhoneNumber
										name="phone"
										placeholder="phoneNumber"
										value={watch('phone')}
										onChange={(e) => {
											setValue('phone', e, { shouldValidate: true, shouldDirty: true });
										}}
										countryCode={watch('country') || 'SA'}
									/>
								</FormItem>
								<FormItem label="email" colSpan={defaultColSpan} required>
									<StringInput placeholder="email" name="email" />
								</FormItem>
							</S.Form>
						</S.Content>
						<S.AddressContainer>
							<Typography variant="p1" fontWeight="700" className="subtitle">
								addressDetail
							</Typography>
							<AddressGroupV2
								formFieldName="address"
								variants={['fillFromGoogleMaps']}
								formMode="edit"
								restrictions={{
									country: {
										isNotDifferent: true,
										code: watch('country')
									}
								}}
								fieldsColspan={{
									address: {
										sm: 12
									},
									country: fullColSpan,
									state: fullColSpan,
									city: fullColSpan,
									district: fullColSpan,
									zipCode: fullColSpan,
									lat: {
										md: 3
									},
									lng: {
										md: 3
									}
								}}
							/>
						</S.AddressContainer>
					</div>

					<S.FormRow id="confirm-form">
						<Button
							label="next"
							variant="blue"
							size="xx-large"
							fullWidth
							onClick={handleSubmit(onSubmit, (errors) => {
								AppLogger.log(errors);
							})}
							disabled={isValid ? false : !isDirty}
							loading={isLoading}
						/>
					</S.FormRow>
				</S.Wrapper>
			</S.Card>
		</FormProvider>
	);
};

export const OrderTable = () => {
	const formMethods = useFormContext();
	const { watch } = formMethods;
	const columns = useMemo(
		() => [
			{
				header: 'productName',
				accessorKey: 'name',
				size: 120,
				showOnMobile: true,
				renderCell: ({ row }) => (
					<S.ItemInfo>
						<S.ProductImage
							url={row.original.image}
							onClick={() => {
								NiceModal.show(ImageModal, { imageUrl: row.original.image });
							}}
						/>

						<Typography fontSize="--paragraph3-font-size" fontWeight="500">
							{row.original.name}
						</Typography>
					</S.ItemInfo>
				)
			},
			{
				header: 'quantity',
				accessorKey: 'quantity',
				size: 75,
				minSize: 60,
				maxSize: 100,
				showOnMobile: true,
				renderCell: ({ row }) => (
					<Typography fontSize="--paragraph3-font-size" fontWeight="500" align="center">
						{row.original.quantity}
					</Typography>
				)
			},
			{
				header: 'totalPrice',
				accessorKey: 'total',
				size: 90,
				minSize: 80,
				maxSize: 100,
				showOnMobile: true,
				renderCell: ({ row }) => (
					<Typography
						fontSize="--paragraph3-font-size"
						fontWeight="500"
						align="center"
						withCurrency
						currency={watch('currency')}>
						{row.original.total}
					</Typography>
				)
			}
		],
		[]
	);
	return <Table columns={columns} data={watch('orderItems')} />;
};

export default ContactDetailsTab;
